// Import style
import './style.scss';

// Import components
import Field from '@automator/builder/_legacy/js/field';

// Import utilities
import { html, render } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';

// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, _x } from '@wordpress/i18n';

// Import definitions
import { getIntegration } from '@automator/builder/definitions/integrations';

// Import recipe builder services
import { getItem } from '@automator/builder/services/item';

import {
	getConditionPostData,
	getConditionsGroupById,
	updateRemoteConditionsMeta,
	getConditionById
} from '@automator/builder/_legacy/js/item/conditions/utilities';

import { parseDynamicSentence } from '@automator/builder/_legacy/js/utilities';
import Actions from '@automator/builder/_legacy/js/item/actions';

import './components/form';

/**
 * Creates a modal with the options of the condition
 */
export default class ItemConditionsModalOptions {
	/**
	 * Create a reference to the dialog
	 *
	 * @type {Ref}
	 */
	$dialog = createRef();

	/**
	 * Create a reference to the dialog
	 *
	 * @type {Ref}
	 */
	$saveButton = createRef();

	/**
	 * Create a reference to the form
	 *
	 * @type {Ref}
	 */
	$form = createRef();

	/**
	 * Invokes the main methods that create the modal
	 *
	 * @param {Object} options                   The options
	 * @param {String} options.conditionsGroupId The ID of the conditions groups
	 * @param {String} options.integrationCode   The unique code of the integration
	 * @param {String} options.conditionCode     The unique code of the condition
	 * @param {String} options.conditionId       The unique ID of the condition. This is option,
	 *                                           and you can leave empty to create a new condition.
	 * @param {String} options.comingFrom        A string that identifies if this modal was created
	 *                                           using the "Filter" button, the "+ Add" button, or
	 *                                           if the user is editing a condition pill
	 * @return {undefined}
	 */
	constructor( { conditionsGroupId, integrationCode, conditionCode, conditionId, comingFrom } ) {
		// Get the conditions group
		const conditionsGroup = getConditionsGroupById( {
			conditionsGroupId: conditionsGroupId,
		} );

		// Check if it's defined
		if ( isEmpty( conditionsGroup ) ) {
			return;
		}

		// Save it
		this.conditionsGroup = conditionsGroup;

		// Save "coming from"
		// This will basically tell us if the user created this modal by clicking on the "Filter
		// button in actions, if they clicked on the "+Add" button in the conditions block, or
		// if they are editing a condition pill
		this.comingFrom = comingFrom;

		// Get condition
		// If conditionId is defined, then get it from the list of conditions
		if ( ! isEmpty( conditionId ) ) {
			// Get the data of the condition
			this.condition = getConditionById( {
				conditionsGroupId: conditionsGroupId,
				conditionId: conditionId,
			} );

			// Get condition code and integration code from condition
			if ( isDefined( this.condition ) ) {
				// Save unique identifiers
				this.integrationCode = this.condition.integrationCode;
				this.conditionCode = this.condition.conditionCode;
			}

			// Set the modal mode to "Edit"
			// This means the user is EDITING the condition, not creating a new one
			this.modalMode = 'edit';
		} else {
			// Save unique identifiers
			this.integrationCode = integrationCode;
			this.conditionCode = conditionCode;

			// Create a new condition
			this.condition = this.createCondition();

			// Set the modal mode to "Create"
			// This means the user is CREATING a condition, not editing one
			this.modalMode = 'create';
		}

		// Check if the condition exists
		// Check if the condition post data exists
		if ( ! isEmpty( this.condition ) && ! isEmpty( this.conditionPostData ) ) {
			// Create the dialog
			this.render();
		}
	}

	/**
	 * Renders the modal
	 *
	 * @return {undefined}
	 */
	render() {
		render(
			html`
				<uo-dialog
					heading="${ escapeHTML( __( 'Configure the rule', 'uncanny-automator' ) ) }"
					force-manual-close
					size="large"
					open

					${ ref( this.$dialog ) }
				>
					<div
						class="uap-conditions-options"
						data-integration="${ this.integrationCode }"
						data-condition="${ this.conditionCode }"
					>
						${ this._templateHeader }

						<uap-builder-legacy-condition-form
							.condition=${ this.condition }
							.fields=${ this.condition.fields }

							parent-id=${ this.parentID }
							integration-code=${ this.integrationCode }
							condition-code=${ this.conditionCode }

							${ ref( this.$form ) }
						></uap-builder-legacy-condition-form>
					</div>

					<uo-button
						slot="primary-action"

						${ ref( this.$saveButton ) }

						@click=${ () => {
							// Save options
							this.saveOptions();
						} }
					>
						${ escapeHTML( __( 'Save filter', 'uncanny-automator' ) ) }
					</uo-button>

					<uo-button
						slot="secondary-action"
						dialog-action="close"
						color="secondary"

						@click=${ () => {
							// Remove condition
							this.maybeRemoveCondition();

							// Maybe delete the conditions group
							this.maybeRemoveConditionsGroup();
						} }
					>
						${ escapeHTML( _x( 'Cancel', 'Verb', 'uncanny-automator' ) ) }
					</uo-button>

				</uo-dialog>
			`,
			this.$wrapper,
			{ host: this }
		);
	}

	/**
	 * Returns the header of the template
	 *
	 * @return {TemplateResult} The TemplateResult of the header
	 */
	get _templateHeader() {
		return html`
			<div class="uap-conditions-options-header">
				<div class="uap-conditions-options-header-integration">
					<ua-builder-integration-icon
						code=${ this.condition.integrationCode }
						size="xsmall"
						plain
					></ua-builder-integration-icon>

					<uo-chip size="small" filled>
						${ getIntegration( this.condition.integrationCode ).name }
					</uo-chip>
				</div>

				<div class="uap-conditions-options-header-sentence">
					${
						parseDynamicSentence( {
							dynamicSentence: this.conditionPostData.dynamic_name,
							fields: this.condition.fields,
							itemID: this.conditionsGroup.actionWithLowestPriority
						} )
					}
				</div>
			</div>
		`;
	}
	
	/**
	 * Saves options
	 *
	 * @return {undefined}
	 */
	saveOptions() {
		if ( ! this.$form.value ) {
			return;
		}

		// Get the form
		const $form = this.$form.value;

		// Check if the fields are valid
		if ( $form.areFieldsValid ) {
			// Save fields in the condition
			this.condition.fields = $form.unsavedFieldValues;

			// Add loading animation to the button
			this.$saveButton.value.loading = true;

			// Update the data in the server
			updateRemoteConditionsMeta( {
				onSuccess: ( response ) => {
					// Remove loading animation to the button
					this.$saveButton.value.loading = false;

					// Check if the task was successful
					if ( response.success ) {
						// Close the modal
						this.$dialog.value.close();
					}

					new Actions();
				},
				onFail: () => {
					// Remove loading animation to the button
					this.$saveButton.value.loading = false;
				},
			} );
		}
	}

	/**
	 * Creates the condition and adds it to the conditions group
	 *
	 * @return {Condition} The created condition
	 */
	createCondition() {
		// Add the condition
		return this.conditionsGroup.addCondition( {
			integrationCode: this.integrationCode,
			conditionCode: this.conditionCode,
			fields: {},
		} );
	}

	/**
	 * Remove the created condition
	 *
	 * @return {undefined}
	 */
	maybeRemoveCondition() {
		// Check if the user was CREATING the condition
		if ( this.modalMode == 'create' ) {
			// Remove the condition using the condition ID
			this.conditionsGroup.removeCondition( {
				conditionID: this.condition.id,
			} );
		}
	}

	/**
	 * Checks if the conditions group doesn't have any conditions and removes it
	 *
	 * @return {undefined}
	 */
	maybeRemoveConditionsGroup() {
		// Check if the user clicked this modal by clicking on the "Filter" button in actions
		// We're deleting the conditions group if they come from the "Filter" button in actions because
		// we don't want to keep the group created after they click on "Cancel"
		if ( this.comingFrom == 'action-filter-button' ) {
			// Check if the conditions groups have conditions
			if ( ! this.conditionsGroup.hasConditions ) {
				// Remove it
				this.conditionsGroup.destroy();
			}
		}
	}

	/**
	 * The wrapper where we're going to add the dialog
	 * We're going to use this until the whole UI moves to Lit
	 *
	 * @return {Node} A wrapper
	 */
	get $wrapper() {
		// Check if we already created one
		if ( ! isDefined( this.#$wrapper ) ) {
			// Create the element
			this.#$wrapper = document.createElement( 'div' );

			// Append it to the DOM
			document.body.append( this.#$wrapper );
		}

		// Return the wrapper
		return this.#$wrapper;
	}

	/**
	 * Returns the condition post data
	 *
	 * @return {Object} Object with useful data about the condition (like sentence and fields)
	 */
	get conditionPostData() {
		return getConditionPostData( {
			integrationCode: this.integrationCode,
			conditionCode: this.conditionCode,
		} );
	}

	/**
	 * Returns the parent ID of the condition
	 *
	 * @return {String} The parent ID of the condition
	 */
	get parentID() {
		const filterCondition = getItem( 'filter-condition', this.condition.id );

		const filter = getItem( 'filter', this.conditionsGroup.id );

		if ( filterCondition ) {
			return this.conditionsGroup.id;
		} 
		
		if ( filter ) {
			return this.conditionsGroup.parentID;
		}
		
		if ( this.conditionsGroup.actionWithLowestPriority ) {
			return UncannyAutomator._recipe.recipe_id;
		}
			
		return UncannyAutomator._recipe.recipe_id;
	}

	#$wrapper;
}
